import { faSparkles } from '@hg-akademie-ban/ban-elevate-assets/icons/solid';
import { Add, FolderCopyOutlined } from '@mui/icons-material';
import { Button, Paper, Tooltip, Typography } from '@mui/material';
import { RefObject, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Tag } from '@/components/ui/tag.tsx';

import { url } from '../../../appRouter/url';
import { planungsformate, type ThemaFromThemaRouter } from '../../../dtos';
import { themaDetailsPageContent } from '../../../pages/ThemaDetailsPage/ThemaDetailsPage.content.ts';
import { isAblaufTemplateMissing, isTermingruppe, printCservFormat } from '../../../utils';
import { isInViewport } from '../../../utils/isInViewport.ts';
import { formatBuchungsnummern } from '../../../utils/themaProdukte.ts';
import { LabelValue } from '../LabelValue.tsx';
import { ModulIdLabelValue } from '../ModulIdLabelValue.tsx';
import { PendantLabelValue } from '../PendantLabelValue.tsx';
import { StickyWrapperWithBlur } from '../StickyWrapper.tsx';
import { LeistungTag } from './LeistungTag.tsx';
import { NavigationTab } from './NavigationTab.tsx';
import { content } from './ThemaHeader.content';
import { getOnlinePraesenzTag } from './ThemaHeader.utils.tsx';

const isAnlegenAllowed = (
  thema: ThemaFromThemaRouter,
): { enabled: true; disabledReason: null } | { enabled: false; disabledReason: string } => {
  const hasNoLeistungstemplates = thema.leistungstemplates.length === 0;

  if (hasNoLeistungstemplates && !thema.manuellerAusschreibungsprozess) {
    return {
      enabled: false,
      disabledReason: themaDetailsPageContent.tooltips.missingLeistungstemplates,
    };
  }

  if (!isTermingruppe(thema.planungsformat) && isAblaufTemplateMissing(thema)) {
    return {
      enabled: false,
      disabledReason: themaDetailsPageContent.tooltips.missingAblaufTemplate,
    };
  }

  return { enabled: true, disabledReason: null };
};

type ThemaHeaderProps = {
  readonly thema: ThemaFromThemaRouter;
  readonly navigation: { label: string; ref: RefObject<HTMLElement | null> }[];
};

export const ThemaHeader: React.FC<ThemaHeaderProps> = ({ thema, navigation }: ThemaHeaderProps) => {
  const navigate = useNavigate();

  const { enabled: anlegenAllowed, disabledReason: anlegenDisabledReason } = isAnlegenAllowed(thema);

  const handleClick = (): void => {
    if (isTermingruppe(thema.planungsformat)) {
      void navigate(url.thema.durchgangPlanung(thema.akaThemaId));
    } else {
      void navigate(url.thema.veranstaltungPlanung(thema.akaThemaId));
    }
  };

  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const calculateCurrentTabIndex = () => {
    setCurrentTabIndex(navigation.findIndex((nav) => isInViewport(nav.ref)));
  };

  const entityLabel = isTermingruppe(thema.planungsformat) ? 'Durchgang' : 'Veranstaltung';

  // useEffect to add scroll listening
  useEffect(() => {
    document.addEventListener('scroll', calculateCurrentTabIndex, true);
    return () => document.removeEventListener('scroll', calculateCurrentTabIndex, true);
  });

  const [{ strukturellesFormat }] = thema.produkte;
  return (
    <StickyWrapperWithBlur sx={{ width: '100%' }}>
      <div className="relative z-1100">
        <Paper
          sx={{
            p: 2,
            backgroundColor: (theme) => theme.palette.text.brand,
          }}
        >
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-8">
              <div className="flex justify-between items-center">
                <div className="flex gap-16 items-center">
                  <FolderCopyOutlined fontSize="large" sx={{ color: 'text.secondaryOnBrand' }} />
                  <Typography variant="headingXs" color="textSecondaryOnBrand">
                    {thema.titel}
                  </Typography>
                  <div className="flex gap-4">
                    {strukturellesFormat === 'KI_LOOP_PRODUKT' && (
                      <Tag label="KI Loop" variant="brandSubtle" icon={faSparkles} />
                    )}
                    {thema.akaModulId && <Tag label="Modul" variant="brandSubtle" />}
                    {getOnlinePraesenzTag(thema)}
                    <LeistungTag leistungtemplates={thema.leistungstemplates} />
                  </div>
                </div>

                <Tooltip title={anlegenDisabledReason} placement="left" arrow disableInteractive>
                  <div>
                    <Button
                      disabled={!anlegenAllowed}
                      onClick={handleClick}
                      variant="contained"
                      size="small"
                      sx={(theme) => ({
                        background: theme.palette.surface.background,
                        '&:hover': { background: theme.palette.surface.brandLightHover },
                        '&:disabled': {
                          background: theme.palette.surface.disabledSolid,
                          color: theme.palette.text.disabledSolid,
                        },
                        color: 'text.primary',
                        py: 0.75,
                        fontSize: '12px',
                      })}
                      startIcon={<Add />}
                    >
                      {entityLabel} anlegen
                    </Button>
                  </div>
                </Tooltip>
              </div>
              {(thema.themenblock ?? thema.themenbereich) && (
                <Typography variant="labelLight" color="textSecondaryOnBrand">
                  {thema.themenblock}&nbsp; • &nbsp;{thema.themenbereich}
                </Typography>
              )}
            </div>

            <div className="grid items-center gap-8 grid-cols-3">
              {/* row 1 */}
              <LabelValue color="brand" label={content.format} value={printCservFormat(thema)} />
              {thema.akaModulId ? <ModulIdLabelValue akaModulId={thema.akaModulId} /> : <div />}
              <LabelValue
                color="brand"
                label="Gehört zu AKA-Produkt-ID"
                value={thema.produkte.map((p) => p.akaProduktId).join(', ')}
              />

              {/* row 2 */}
              {thema.planungsformat === planungsformate.EINZELTERMIN && thema.akaModulId ? (
                <LabelValue color="brand" label="Besonderheiten" value="Modul mit Einzelterminplanung" />
              ) : (
                <div />
              )}
              <LabelValue color="brand" label="SAP-ID (D)" value={thema.veranstaltungstypSapId} />
              <LabelValue color="brand" label={content.buchungsnummer} value={formatBuchungsnummern(thema)} />

              {/* row 3 */}
              <PendantLabelValue thema={thema} />
              {thema.planungsinformation?.musskopplungReihenfolgeAkaProduktIds &&
                thema.planungsinformation.musskopplungReihenfolgeAkaProduktIds.length > 0 && (
                  <LabelValue
                    label="Muss-Kopplung"
                    value={
                      <div className="flex gap-8">
                        {thema.planungsinformation.musskopplungReihenfolgeAkaProduktIds.map((akaProduktId) => (
                          <Link
                            to={url.thema.detailsRedirectByAkaProduktId(akaProduktId)}
                            key={akaProduktId}
                            style={{ textDecorationColor: 'white' }}
                          >
                            <Typography variant="labelLight" color="textPrimaryOnBrand">
                              {akaProduktId}
                            </Typography>
                          </Link>
                        ))}
                      </div>
                    }
                    color="brand"
                  />
                )}
            </div>
          </div>
        </Paper>

        <Paper
          sx={{
            mt: 1,
            width: '100%',
            borderColor: 'transparent',
            boxShadow:
              '0 0 1px 1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div className="flex justify-between px-16 py-8">
            {navigation.map((nav, index) => (
              <NavigationTab key={nav.label} ref={nav.ref} isSelected={currentTabIndex === index} label={nav.label} />
            ))}
          </div>
        </Paper>
      </div>
    </StickyWrapperWithBlur>
  );
};
